/**
 * Cloudflare Turnstile Hook
 *
 * Simplified state management hook for Cloudflare Turnstile CAPTCHA verification.
 * Unlike reCAPTCHA v3, Turnstile doesn't require a provider wrapper or manual execution.
 * The Turnstile component handles verification and calls onSuccess with a token.
 *
 * This hook provides:
 * - Token state management
 * - Verification status tracking
 * - Reset functionality for form resubmission
 *
 * @example
 * ```javascript
 * import { useTurnstile } from "@shared/hooks";
 * import { Turnstile } from "@marsidev/react-turnstile";
 *
 * function ContactForm() {
 *   const { token, setToken, isVerifying, setIsVerifying, resetToken } = useTurnstile();
 *
 *   const handleSubmit = async (data) => {
 *     if (!token) {
 *       alert("Please complete verification");
 *       return;
 *     }
 *
 *     setIsVerifying(true);
 *     const result = await sendForm({ ...data, turnstileToken: token });
 *     if (result.success) resetToken();
 *     setIsVerifying(false);
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Turnstile
 *         siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
 *         onSuccess={setToken}
 *         onError={() => setToken(null)}
 *         onExpire={() => setToken(null)}
 *       />
 *       <button disabled={!token || isVerifying}>Submit</button>
 *     </form>
 *   );
 * }
 * ```
 */

import { useState, useCallback } from "react";

/**
 * Hook for managing Cloudflare Turnstile verification state
 *
 * @returns {Object} Turnstile state and setters
 * @returns {string|null} token - The verification token from Turnstile
 * @returns {Function} setToken - Set the verification token
 * @returns {boolean} isVerifying - Whether form is currently being verified/submitted
 * @returns {Function} setIsVerifying - Set verification status
 * @returns {Function} resetToken - Reset token and verification status
 */
export function useTurnstile() {
  const [token, setToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  /**
   * Reset token and verification status
   * Useful after form submission or when user cancels
   */
  const resetToken = useCallback(() => {
    setToken(null);
    setIsVerifying(false);
  }, []);

  return {
    token,
    setToken,
    isVerifying,
    setIsVerifying,
    resetToken,
  };
}
